import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import "../../assets/styles/colors.css";

/**
 * Generic modal with a text input (textarea), optional description,
 * and configurable title + positive/negative action buttons.
 */
function TextInputModal({
  show,
  onClose,
  title,
  description,
  placeholder,
  value,
  onChange,
  positiveText,
  negativeText,
  onConfirm,
  loading = false,
  minRows = 4,
  errorContent = null,
  actionBadge = null,
  positiveDisabled = false,
}) {
  const { t } = useTranslation();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!show) return;
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, onClose]);

  if (!show) return null;

  const handleConfirm = () => {
    if (onConfirm) onConfirm(value);
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-[90%] max-w-[440px] rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-card)] shadow-[0_0_20px_var(--color-shadow-primary)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-6 py-4">
          <h3 className="text-lg font-semibold text-[var(--color-text-white)]">
            {typeof title === "string"
              ? t(title, { defaultValue: title })
              : title}
          </h3>
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center text-xl text-[var(--color-primary)] transition-colors hover:border-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 disabled:opacity-50"
            onClick={onClose}
            disabled={loading}
            aria-label={t("Close")}
          >
            ×
          </button>
        </div>

        <div className="px-6 py-5">
          {description != null && description !== "" && (
            <p className="text-sm text-[var(--color-text-muted)] mb-3">
              {typeof description === "string"
                ? t(description, { defaultValue: description })
                : description}
            </p>
          )}
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={
              placeholder
                ? t(placeholder, { defaultValue: placeholder })
                : undefined
            }
            rows={minRows}
            className="w-full min-h-[100px] rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2.5 text-sm text-[var(--color-text-white)] placeholder-[var(--color-text-muted)] focus:border-[var(--color-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] resize-y"
            disabled={loading}
            autoFocus
          />
          {errorContent}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-[var(--color-border)] px-6 py-4">
          {actionBadge}
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-5 py-2.5 rounded-lg border border-[var(--color-border)] text-sm text-[var(--color-text-muted)] transition-all hover:border-[var(--color-primary)]/50 hover:text-[var(--color-primary)] disabled:opacity-50"
          >
            {typeof negativeText === "string"
              ? t(negativeText, { defaultValue: negativeText })
              : negativeText}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading || positiveDisabled}
            className="px-5 py-2.5 rounded-lg border border-[var(--color-primary)] bg-[var(--color-primary)] text-white dark:text-black text-sm font-medium transition-all hover:shadow-[0_0_10px_var(--color-shadow-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading && (
              <svg
                className="w-4 h-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {typeof positiveText === "string"
              ? t(positiveText, { defaultValue: positiveText })
              : positiveText}
          </button>
        </div>
      </div>
    </div>
  );
}

TextInputModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  placeholder: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  positiveText: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
    .isRequired,
  negativeText: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
    .isRequired,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  minRows: PropTypes.number,
  errorContent: PropTypes.node,
  actionBadge: PropTypes.node,
  positiveDisabled: PropTypes.bool,
};

export default TextInputModal;
